import { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Bell, AlertTriangle } from 'lucide-react';
import { useConsents, useUpdateConsents } from '@/api/hooks/useConsents';
import { api } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

export function SettingsPage() {
  const { data, isLoading } = useConsents();
  const updateConsents = useUpdateConsents();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const consents = data?.consents;

  const handleConsentChange = (key: string, value: boolean) => {
    updateConsents.mutate({ [key]: value });
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    setDeleting(true);
    try {
      await api.delete('/profile');
      // Cookie cleared server-side; redirect to login
      window.location.href = '/portal/login';
    } catch (err: unknown) {
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to delete account'
      );
      setDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-copper border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-navy">Settings</h1>
        <p className="mt-1 text-gray-500">
          Manage your consent preferences and account
        </p>
      </div>

      <div className="space-y-6">
        {/* Consent Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-copper" />
              Data Consent
            </CardTitle>
            <CardDescription>
              Control how your data is used. Required consents cannot be disabled.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Required consents */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-navy">Bloodwork Analysis</Label>
                  <p className="text-xs text-gray-500">
                    Allow automated analysis of your uploaded bloodwork data
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">Required</span>
                  <Switch checked disabled />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-navy">De-identified Research</Label>
                  <p className="text-xs text-gray-500">
                    Allow de-identified data to be used for research purposes
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">Required</span>
                  <Switch checked disabled />
                </div>
              </div>
            </div>

            <Separator />

            {/* Optional consents */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-navy">Re-test Reminders</Label>
                  <p className="text-xs text-gray-500">
                    Receive email reminders when it's time for new bloodwork
                  </p>
                </div>
                <Switch
                  checked={consents?.email_reminders ?? false}
                  onCheckedChange={(v) => handleConsentChange('email_reminders', v)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-navy">Product Recommendations</Label>
                  <p className="text-xs text-gray-500">
                    Receive supplement and product recommendations via email
                  </p>
                </div>
                <Switch
                  checked={consents?.marketing ?? false}
                  onCheckedChange={(v) => handleConsentChange('marketing', v)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notifications (placeholder) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-slate-brand" />
              Notifications
            </CardTitle>
            <CardDescription>
              Configure how you receive notifications
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-400 py-4 text-center">
              Notification preferences coming soon.
            </p>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Delete Account
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  This will permanently delete all your data including bloodwork history
                </p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete Account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete your account? This action cannot be
              undone. All of your data, including bloodwork history, analysis
              results, and protocol information, will be permanently removed.
            </DialogDescription>
          </DialogHeader>

          {deleteError && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
            >
              {deleteError}
            </motion.div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Yes, Delete My Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
